import { CSSProperties, MouseEvent } from "react";
import { GetAntIcon } from "../../../utils/ant_icons";
import ClampedText from "../ClampedText";
import {
	getFindingPerson,
	getFindingStatus,
} from "../../../utils/reporting/findingFilters";
import {
	ReportItemAttachments,
	ReportItemDeadline,
	ReportItemOutcome,
	ReportItemPerson,
	ReportItemRecommendedAction,
	ReportItemStages,
	ReportItemStatusTag,
	ReportItemTags,
	ReportItemViewProps,
	getReportItemDocuments,
} from "./ReportItemParts";

// A click on one of the card's own buttons, such as an attachment, is that
// button's, and one that ends a text selection only selects. A click in a
// tooltip reaches the card through React but isn't on it.
const isCardClick = (event: MouseEvent<HTMLElement>) =>
	event.currentTarget.contains(event.target as Node) &&
	!(event.target as Element).closest("button, a") &&
	!window.getSelection()?.toString();

// How many attachments a card lists before "+N more": cards in a row share
// their footer's height, so one long list would stretch its neighbours.
const CARD_ATTACHMENT_LIMIT = 3;

// The Cards view of a report's items: one card per item, filling the width.
// A card the user can work on opens in the report's drawer when clicked;
// keyboard users have the button on its title.
function ReportItemCardList(props: Readonly<ReportItemViewProps>) {
	const {
		kind,
		entries,
		showStatus,
		showType,
		stageReport,
		canEditItem,
		onEditItem,
		onOpenDocument,
	} = props;
	const recommendedField = kind.recommendedActionField;
	// How many sections each card has, for the stylesheet's subgrid span: the
	// head, description, person and deadline, stages, the recommended action
	// where the kind has one, the outcome and the footer.
	const sectionCount = recommendedField ? 7 : 6;

	return (
		<div
			className="report-items__cards"
			style={{ "--report-items-card-rows": sectionCount } as CSSProperties}
		>
			{entries.map((entry) => {
				const { item, index } = entry;
				const title = kind.getTitle(index);
				const description = item?.[kind.descriptionField] || "—"; // NOSONAR: empty text shows a dash
				const open =
					onEditItem && canEditItem?.(item)
						? () => onEditItem(index)
						: undefined;
				// The title button's target, widened for the mouse.
				const openFromCard =
					open &&
					((event: MouseEvent<HTMLElement>) => {
						if (isCardClick(event)) open();
					});

				return (
					<article // NOSONAR: only widens the mouse target; keyboard users have the title button
						key={index}
						className={
							open
								? "report-items__card report-items__card--editable"
								: "report-items__card"
						}
						aria-label={title}
						onClick={openFromCard}
					>
						<div className="report-items__card-head">
							<h3 className="report-items__number">
								{open ? (
									<button
										type="button"
										className="report-items__card-open"
										onClick={open}
									>
										<span className="sr-only">Edit </span>
										{title}
									</button>
								) : (
									title
								)}
							</h3>
							<ReportItemTags item={item} kind={kind} showType={showType} />
							{showStatus && (
								<span className="report-items__card-status">
									<ReportItemStatusTag status={getFindingStatus(item)} />
								</span>
							)}
						</div>
						<div className="report-items__description">
							<ClampedText
								text={description}
								className="report-items__description-text"
								buttonClassName="report-items__more"
							/>
						</div>
						<dl className="report-items__facts">
							<div>
								<dt>{getFindingPerson(item).label}</dt>
								<dd>
									<ReportItemPerson item={item} />
								</dd>
							</div>
							<div>
								<dt>Deadline</dt>
								<dd>
									<ReportItemDeadline entry={entry} />
								</dd>
							</div>
						</dl>
						<ReportItemStages item={item} report={stageReport} kind={kind} />
						{recommendedField && (
							<ReportItemRecommendedAction text={item?.[recommendedField]} />
						)}
						{/* There even when empty, so every card has the same sections
						    for its neighbours to line up with. */}
						<div className="report-items__card-outcome">
							<ReportItemOutcome
								entry={entry}
								kind={kind}
								showStatus={showStatus}
							/>
						</div>
						<div className="report-items__card-foot">
							<ReportItemAttachments
								documents={getReportItemDocuments(item)}
								onOpenDocument={onOpenDocument}
								limit={CARD_ATTACHMENT_LIMIT}
							/>
							{open && (
								<span className="report-items__edit-hint" aria-hidden="true">
									{GetAntIcon("edit")}
									Edit
								</span>
							)}
						</div>
					</article>
				);
			})}
		</div>
	);
}

export default ReportItemCardList;
